import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";

/**
 * Is the booking being made right now covered by the customer's organization
 * (#1926)?
 *
 * The single reader of that fact in this app. Every surface that renders
 * differently for a covered booking — the price, the summary, the success
 * copy — asks here, so they cannot drift apart from each other.
 *
 * **The app never decides this.** The answer comes from the availability
 * response (`coveredForMe`), computed server-side with the same predicate that
 * order creation re-derives from the session. A client that worked it out
 * itself would eventually disagree with the booking the server actually
 * creates, and the person would be shown a price they were never charged — or
 * worse, no price for a booking that was.
 *
 * `undefined` (the route carried no service, so the server could not answer)
 * renders exactly like "not covered": the normal flow, which is what every
 * booking did before this feature existed.
 */
export const useIsCoveredBooking = (): boolean =>
    useSelector(
        // Optional all the way down: this hook is read from the SHARED booking
        // summary, which several flows render, and not every one of them mounts
        // the apartment slice. A missing slice is "not covered", never a crash
        // in the middle of a checkout.
        //
        // Eligible AND not declined: a member may book privately and pay like
        // anyone else ("Privat buchen – ich zahle selbst",
        // docs/plans/member-private-booking.md). Every covered surface reads
        // this hook, so they all switch back to the normal flow together.
        (state: RootState) =>
            state.apartment?.selectedApartment?.coveredForMe === true
            && !selectBookPrivately(state)
    );

/**
 * Did the member choose "Privat buchen – ich zahle selbst" for the room being
 * booked? The one reader of that choice — the hook above, the select and the
 * request builder all ask here. A choice made for another room does not count.
 */
export const selectBookPrivately = (state: RootState): boolean => {
    const roomId = state.apartment?.selectedApartment?.id;
    return !!roomId && state.bookingCreation?.orderData?.bookPrivatelyFor === roomId;
};

/**
 * The member's coverage choice as a request carries it — ONE builder for the
 * order request (`createVenueReservationFlow`) and the price preview
 * (`BookingSummaryShared`), so the quote and the booking are decided on the
 * same input (ADR-0008 amendment 2026-10-22, refinement 8: a covered booking
 * is priced at the covering organisation's class). Both servers re-derive
 * coverage from the session; these fields only decline (`bookPrivately`) or
 * state a preference among several covering organisations.
 */
export const coverageChoiceFields = (
    coveringOrganizationId: string | null | undefined,
    bookPrivately: boolean
): { coveringOrganizationId?: string; bookPrivately?: true } => ({
    ...(coveringOrganizationId && !bookPrivately ? { coveringOrganizationId } : {}),
    ...(bookPrivately ? { bookPrivately: true as const } : {}),
});

/**
 * Could this booking be covered — before the member's choice? Drives only the
 * "Abgerechnet über" select, which must stay visible after "privat" is chosen
 * so the member can switch back. Everything else reads `useIsCoveredBooking`.
 */
export const useIsCoverageEligible = (): boolean =>
    useSelector(
        (state: RootState) => state.apartment?.selectedApartment?.coveredForMe === true
    );

/**
 * The organizations the logged-in customer may book on behalf of. Used to
 * attribute a covered booking when they belong to more than one — with a single
 * membership there is nothing to ask (plan §4).
 *
 * **Only organizations with a live contract** (PO, 2026-08-25 — #2165). Coverage
 * is granted under a space agreement, so an organization without one cannot
 * cover anything and the server refuses to attribute a booking to it. Offering
 * it anyway is worse than not offering it: the picker auto-selects the first
 * entry, so a member of one contracted and one ended organization was shown
 * "covered by your organization" on a booking that then came out charged.
 *
 * Where the filter leaves exactly one there is no question to ask and the
 * booking is simply covered; where it leaves none the person books and pays like
 * anyone else, with no dropdown and no message — an employee's checkout screen
 * is the wrong place to learn about their employer's contract status. The
 * membership itself stays visible on their account page.
 *
 * `!== false`, not `=== true`, and that is deliberate. The app and the app
 * service deploy separately: against a service that does not yet send the field
 * a strict check would empty this list for EVERYONE and un-cover every member at
 * once. Treating only an explicit `false` as disqualifying degrades to the
 * previous behaviour instead — the bug this fixes, not a worse one. The server
 * is the authority either way; it refuses the attribution regardless of what the
 * picker offered.
 */
export const useCoveringOrganizations = (): {
    id: string;
    name: string;
    /** #1810: the cost centre a covered booking for this organisation starts with. */
    defaultCostCenter: string | null;
}[] =>
    useSelector((state: RootState) => {
        // Option C (docs/plans/coverage-per-room-assignment.md): coverage is per
        // ROOM — one organization may cover this room and only have access to
        // the next — so the server's per-room list decides when it is there.
        // The per-organization contract flag is the fallback for an app service
        // that does not send the list yet.
        const coveringHere = state.apartment?.selectedApartment?.coveringOrganizationIds;
        return (state.account?.profile?.memberships ?? [])
            .filter((membership) => membership.status?.toLowerCase() === "approved")
            .filter((membership) =>
                Array.isArray(coveringHere)
                    ? coveringHere.includes(membership.organizationId)
                    : membership.hasActiveContract !== false
            )
            .map((membership) => ({
                id: membership.organizationId,
                name: membership.organizationName,
                defaultCostCenter: membership.defaultCostCenter ?? null,
            }));
    });

export default useIsCoveredBooking;
